// Persistence for the domain-match signup banner (ENG-2509, Phase 0b).
//
// The `domain_match` block arrives from BOTH POST /api/me/bootstrap (once, on
// the fresh self-serve signup call) and GET /api/me (recomputed every load, to
// catch a LATE VERIFIER who bootstrapped while unverified). The banner renders
// from the app shell, so the match is stashed in localStorage keyed by uid —
// this survives the post-signup reload and lets any load re-surface it.
//
// A `requested` flag on the stash drives the "request sent" confirmation. A
// separate DISMISSAL tombstone records "this user chose Start-my-own for this
// workspace" so a later GET /api/me — which still returns the match (they
// never joined and never filed a request) — can't resurrect the banner. Both
// carry a max-age guard so nothing nags forever.

"use client";

const KEY = "da_domain_match";
const DISMISSED_KEY = "da_domain_match_dismissed";
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

export type StoredDomainMatch = {
  uid: string;
  tenantId: string;
  name: string;
  // True once the join request was filed — the banner then shows the
  // "request sent" confirmation (still dismissible) instead of the choice.
  requested: boolean;
  ts: number;
};

type Dismissal = { uid: string; tenantId: string; ts: number };

function readDismissal(): Dismissal | null {
  try {
    const raw = window.localStorage.getItem(DISMISSED_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as Partial<Dismissal>;
    if (
      typeof d.uid !== "string" ||
      typeof d.tenantId !== "string" ||
      typeof d.ts !== "number" ||
      Date.now() - d.ts > MAX_AGE_MS
    ) {
      window.localStorage.removeItem(DISMISSED_KEY);
      return null;
    }
    return { uid: d.uid, tenantId: d.tenantId, ts: d.ts };
  } catch {
    return null;
  }
}

/** Stash the match for `uid` (from bootstrap or /api/me) UNLESS the user has
 * already dismissed this exact workspace ("Start my own workspace") — a GET
 * /api/me still returns the match after that choice, and must not resurrect
 * the banner. A no-op if an identical stash is already present so we don't
 * clobber the `requested` flag on a re-fetch. */
export function storeDomainMatch(
  uid: string,
  match: { tenant_id: string; name: string },
) {
  try {
    const dismissed = readDismissal();
    if (dismissed && dismissed.uid === uid && dismissed.tenantId === match.tenant_id) {
      return; // user chose Start-my-own for this firm — stay quiet
    }
    const current = readDomainMatch(uid);
    if (current && current.tenantId === match.tenant_id) {
      return; // already stashed (keep any `requested` flag intact)
    }
    const stored: StoredDomainMatch = {
      uid,
      tenantId: match.tenant_id,
      name: match.name,
      requested: false,
      ts: Date.now(),
    };
    window.localStorage.setItem(KEY, JSON.stringify(stored));
  } catch {
    /* private mode / storage disabled — the banner just won't show */
  }
}

/** The stored match IFF it belongs to `uid` and is recent; null otherwise.
 * A mismatched uid clears the entry (a different account signed in on this
 * browser — the old user's firm must not be surfaced to them). */
export function readDomainMatch(uid: string): StoredDomainMatch | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const m = JSON.parse(raw) as Partial<StoredDomainMatch>;
    if (
      typeof m.uid !== "string" ||
      typeof m.tenantId !== "string" ||
      typeof m.name !== "string" ||
      typeof m.ts !== "number" ||
      Date.now() - m.ts > MAX_AGE_MS
    ) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    if (m.uid !== uid) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    return {
      uid: m.uid,
      tenantId: m.tenantId,
      name: m.name,
      requested: Boolean(m.requested),
      ts: m.ts,
    };
  } catch {
    return null;
  }
}

/** Flip the stored match to its post-request state so a reload lands on the
 * "request sent" confirmation instead of re-offering the choice. */
export function markDomainMatchRequested(uid: string) {
  const current = readDomainMatch(uid);
  if (!current) return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ ...current, requested: true }));
  } catch {
    /* ignore */
  }
}

/** Drop the stash. When `dismissal` is given (Start-my-own, or dismissing the
 * request-sent confirmation), also record a tombstone so a later /api/me
 * re-fetch can't resurrect the banner for that workspace. */
export function clearDomainMatch(dismissal?: { uid: string; tenantId: string }) {
  try {
    window.localStorage.removeItem(KEY);
    if (dismissal) {
      const tomb: Dismissal = { ...dismissal, ts: Date.now() };
      window.localStorage.setItem(DISMISSED_KEY, JSON.stringify(tomb));
    }
  } catch {
    /* ignore */
  }
}

/** Suppress (and clear) a cached match when the user is ALREADY in the matched
 * workspace — i.e. the current active-workspace tenant equals the match's
 * tenant. This covers a join request approved via the existing invite flow
 * (the accept page switches the active workspace to the invited tenant and
 * reloads into it, so `GET /api/me` stops returning `domain_match`) as well as
 * any other "already a member of the match tenant" case. Without this the
 * banner would keep rendering from the stale uid-keyed cache until manual
 * dismissal or the 14-day expiry.
 *
 * Returns true when it suppressed (the caller should not render). Tombstones
 * the entry on the way out so the stale cache can't linger or resurrect. A
 * no-op returning false when there's no cached match, no active tenant, or the
 * active tenant isn't the matched one. */
export function suppressMatchIfActiveWorkspace(
  uid: string,
  activeTenantId: string | null | undefined,
): boolean {
  if (!activeTenantId) return false;
  const match = readDomainMatch(uid);
  if (!match || match.tenantId !== activeTenantId) return false;
  clearDomainMatch({ uid, tenantId: activeTenantId });
  return true;
}
